import React, { useState } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import { IPlanFields } from '../../../types/contentful';

import { Text } from '../Text';
import { Box } from '../Box';
import { UnstyledButton } from '../Button';
import RichText from '../RichText';
import CollapsibleContainer from '../Collapsible/CollapsibleContainer';

import { Features } from '../PlansTable/helper';

import {
  PlanText,
  BoxWithBorder,
  ToggleIcon,
  PlanFeature,
  ClickableFlexbox
} from './styles';

import plus from '@/images/plus-white.svg';

const isSavingFeature = (title: string) => {
  return title === 'Greenlight Savings Reward';
};

const getFeatureTitle = (
  name: string,
  title: string,
  copies: Map<string, string | undefined>
) => {
  const copy = copies.get(name);
  if (isSavingFeature(title) && copy) {
    return `${copy} ${title}`;
  } else {
    return title;
  }
};

interface Props extends Omit<IPlanFields, 'features'> {
  features?: Features;
}

export function Plan({
  name,
  subheader,
  color,
  textColor,
  mobileOnlySubheader,
  features
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const whiteColors = ['#FFF', '#FFFFFF', '#fff', '#ffffff'];
  const cleanTextColor =
    textColor && whiteColors.includes(textColor) ? 'white' : 'black';

  const RICH_TEXT_OVERRIDES: RenderNode = {
    [BLOCKS.HEADING_3]: (node, children) => (
      <Text as="p" fontSize="sm" fontWeight="h3" colour={cleanTextColor}>
        {children}
      </Text>
    )
  };

  return (
    <BoxWithBorder color={color || ''} isOpen={isOpen} data-testid={name}>
      <ClickableFlexbox
        justifyContent="space-between"
        alignItems="flex-start"
        onClick={() => setIsOpen(!isOpen)}
        color={color || ''}
        padding={['collapsibleVert', 'm']}
      >
        <div>
          <PlanText
            as="h3"
            fontSize="h2_0"
            fontWeight="h3"
            textColor={textColor || ''}
          >
            {name}
          </PlanText>
          <PlanText
            as="p"
            fontSize="sm"
            fontWeight="h3"
            textColor={textColor || ''}
          >
            {subheader}
          </PlanText>
          {mobileOnlySubheader && (
            <RichText
              bulletColor="white"
              data={mobileOnlySubheader}
              colour={cleanTextColor}
              overrideOptions={RICH_TEXT_OVERRIDES}
            />
          )}
        </div>
        <Box paddingLeft="s" paddingTop="2xs">
          <UnstyledButton>
            <Box paddingTop="2xs" paddingLeft="xs" paddingRight="xs">
              <ToggleIcon
                src={plus}
                alt={isOpen ? 'Close section' : 'Open section'}
                isOpen={isOpen}
              />
            </Box>
          </UnstyledButton>
        </Box>
      </ClickableFlexbox>
      {isOpen && (
        <CollapsibleContainer>
          <Box paddingRight="m" paddingLeft="m">
            {features &&
              features.map(({ title, info, copies }, idx: number) => {
                const copy = copies.get(name ?? '');

                return (
                  <PlanFeature key={idx}>
                    <div>
                      <Text
                        as="h3"
                        fontWeight="h3"
                        fontSize="h3_0"
                        data-blockid="plansTable-mobile-title"
                      >
                        {title &&
                          getFeatureTitle(name as string, title, copies)}
                      </Text>
                      <Box paddingTop="sm">
                        {info && <RichText data={info} />}
                      </Box>
                    </div>
                    {title && !isSavingFeature(title) && copy ? (
                      <Text
                        as="h3"
                        fontWeight="h3"
                        fontSize="sm"
                        style={{ color: color || '' }}
                      >
                        {copy}
                      </Text>
                    ) : (
                      <Box paddingLeft="sm" paddingTop="xs">
                        <svg
                          width="24"
                          height="17"
                          viewBox="0 0 24 17"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-label="Checkmark"
                        >
                          <path
                            d="M2 7.94286L9.03704 15L22 2"
                            stroke={color || ''}
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>
                    )}
                  </PlanFeature>
                );
              })}
          </Box>
        </CollapsibleContainer>
      )}
    </BoxWithBorder>
  );
}
